import {
  type GetChannelsMessagesByCidItem,
  type GetUsersByUserIdResponse,
  useGetAuthMe,
} from "@birdwatch/client";
import { BlockScroll } from "@birdwatch/ui";
import { Button } from "@/components/ui/button/button";
import { CopyTooltip } from "@/components/ui/common/copy";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog/dialog";
import { BwCloseLine, BwCopyLine } from "@/components/ui/icons";
import { Markdown } from "@/components/ui/markdown";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useScreen } from "@/hooks/use-screen";
import { DISPLAY_TIME, formatDate } from "@/libs/date-fns";
import type { ExtractItem } from "@/types/openapi";

type AttachmentItem = ExtractItem<GetChannelsMessagesByCidItem["attachments"]>;

const triggerClassName =
  "text-xs text-text-primary hover:underline cursor-pointer whitespace-nowrap";

export function AudioRecordTranscriptPopover({
  attachment,
  userCreated,
}: {
  attachment: AttachmentItem;
  userCreated: GetUsersByUserIdResponse;
}) {
  const { data: me } = useGetAuthMe().get();
  const summaryText = attachment?.attachmentExtra?.summary ?? "";
  /** Matches Tailwind `sm` (640px) — same breakpoint as `dialog/dialog` bottom sheet vs desktop layout. */
  const useTranscriptDialog = useScreen({ base: true, sm: false });

  const metaLine = (
    <p className="text-sm font-medium text-text-dark-high px-4">
      {userCreated?.name} {me?.id === userCreated?.id ? "(You)" : ""} at{" "}
      {formatDate(attachment?.createdAt ?? null, DISPLAY_TIME)}
    </p>
  );

  const copyButton = (
    <CopyTooltip copy={summaryText} tooltipContent="Copy transcript" asChild>
      <Button
        size="icon-md"
        variant="neutral"
        kind="ghost"
        aria-label="Copy transcript"
      >
        <BwCopyLine />
      </Button>
    </CopyTooltip>
  );

  if (useTranscriptDialog) {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <button type="button" className={triggerClassName}>
            View transcript
          </button>
        </DialogTrigger>
        <DialogContent
          showCloseButton={false}
          className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0 sm:max-h-[94vh] sm:max-w-lg"
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex shrink-0 items-center gap-2 border-b border-border-dark-min px-4 py-3">
              <div className="min-w-0 flex-1">
                <DialogTitle className="p-0 text-left text-base font-medium text-text-dark-max">
                  Transcript (auto-generated)
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Auto-generated transcript of this voice message.
                </DialogDescription>
              </div>
              {copyButton}
              <DialogClose asChild>
                <Button
                  size="icon-md"
                  variant="neutral"
                  kind="ghost"
                  aria-label="Close transcript"
                >
                  <BwCloseLine />
                </Button>
              </DialogClose>
            </div>
            <div className="shrink-0 pt-3">{metaLine}</div>
            <BlockScroll className="min-h-0 flex-1 px-4 pb-4 pt-2">
              <Markdown content={summaryText} className="text-sm" />
            </BlockScroll>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={triggerClassName}>
          View transcript
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-112 rounded-2xl" align="start">
        <div className="flex items-center gap-1 p-3 pb-0 pl-4">
          <p className="font-medium grow">Transcript (auto-generated)</p>
          {copyButton}
          <PopoverClose asChild>
            <Button
              size="icon-md"
              variant="neutral"
              kind="ghost"
              aria-label="Close transcript"
            >
              <BwCloseLine />
            </Button>
          </PopoverClose>
        </div>
        <div className="pt-1">{metaLine}</div>
        <div className="max-h-80 overflow-y-auto p-4 pt-2 pb-4 space-y-4">
          <Markdown content={summaryText} className="text-sm" />
        </div>
      </PopoverContent>
    </Popover>
  );
}
